import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Table, TableBody, TableCell, Divider,
    TableContainer, TableHead, TableRow, IconButton,
    Button, TextField
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import '../../App.css';
import * as common from '../../commons/common';

//민원인 페이지 - 점검기록 조회
export default function ComplaintCheckList() {
    const navigate = useNavigate();
    const { facilityId } = useParams();
    const [checkRslt, setCheckRslt] = useState([]);
    const [facilityName, setFacilityName] = useState('');

    const today = new Date().toISOString().split('T')[0];
    const oneMonthAgo = (() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    })();

    const [startDate, setStartDate] = useState(oneMonthAgo);
    const [endDate, setEndDate] = useState(today);

    // 도메인 확인 - 이전 버튼 숨김 여부
    const isDirectAccess = window.location.hostname === 'ty.unicheck.co.kr' ||
                            window.location.hostname === 'unicheck.dsti.co.kr' ||
                            window.location.hostname === 'jc.unicheck.co.kr' ||
                            window.location.hostname === 'oc.unicheck.co.kr' ;

    useEffect(() => {
        fetchFacilityInfo();
        fetchData();
    }, []);

    const fetchFacilityInfo = async () => {
        try {
            const response = await axios.get(`${common.getApiUrl()}/qr/${facilityId}`);
            if (response.data) {
                setFacilityName(response.data.facilityNm);
            }
        } catch (error) {
            common.handleApiError(error);
        }
    };

    const fetchData = async () => {
        try {
            const response = await axios.get(`${common.getApiUrl()}/qr/result/${facilityId}`, {
                params: { startDate, endDate }
            });
            setCheckRslt(response.data.checkRslt ?? []);
        } catch (error) {
            common.handleApiError(error);
        }
    };

    const handleNaviBack = () => {
        navigate(`/complaint-input/${facilityId}`);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'var(--main-white-color)' }}>
            {/* 헤더 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '70px', padding: '10px 20px', backgroundColor: 'var(--main-blue-color)', color: 'white' }}>
                {!isDirectAccess && (
                    <Box sx={{ position: 'absolute', top: '20px', left: '20px', display: 'flex', alignItems: 'center', cursor: 'pointer', backgroundColor: '#fff', borderRadius: '15px', padding: '0 10px 0 4px' }}
                        onClick={handleNaviBack}>
                        <IconButton sx={{ color: 'var(--main-blue-color)', padding: 0, paddingLeft: '1px' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: '1.1rem', color: 'var(--main-blue-color)' }}>이전</Typography>
                    </Box>
                )}
                {facilityName && (
                    <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {facilityName}
                    </Typography>
                )}
            </Box>
            <Divider />

            {/* 날짜 검색 영역 */}
            <Box sx={{ padding: '10px 12px', backgroundColor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px', backgroundColor: '#fff', borderRadius: '8px' }}>
                <TextField
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    inputProps={{ max: endDate }}
                    sx={{
                        flex: 1,
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': { height: '36px' },
                        '& .MuiOutlinedInput-input': { padding: '0 4px', fontSize: '0.78rem' },
                    }}
                />
                <Typography sx={{ color: '#666', fontWeight: 'bold', flexShrink: 0, fontSize: '0.9rem' }}>~</Typography>
                <TextField
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    inputProps={{ min: startDate, max: today }}
                    sx={{
                        flex: 1,
                        backgroundColor: '#fff',
                        '& .MuiOutlinedInput-root': { height: '36px' },
                        '& .MuiOutlinedInput-input': { padding: '0 4px', fontSize: '0.78rem' },
                    }}
                />
                <Button
                    variant="contained"
                    onClick={fetchData}
                    sx={{
                        flexShrink: 0,
                        minWidth: '52px',
                        height: '36px',
                        padding: '0 10px',
                        fontSize: '0.85rem',
                        backgroundColor: 'var(--main-blue-color)',
                        '&:hover': { backgroundColor: 'var(--main-blue-color)' },
                        boxShadow: 'none',
                    }}
                >
                    조회
                </Button>
            </Box>
            </Box>

            {/* 결과 목록 - 세로 스크롤 */}
            <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#f5f5f5' }}>
                {checkRslt.length > 0 ? (
                    checkRslt.map((record, index) => (
                        <Box key={index} sx={{ margin: '12px 12px', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                            {/* 점검일시 헤더 */}
                            <Box sx={{ padding: '10px 16px', backgroundColor: '#f0f4ff' }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--main-blue-color)' }}>
                                    점검일시 : {record.checkTime}
                                </Typography>
                            </Box>
                            {/* 점검항목 테이블 */}
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', padding: '8px' }}>점검항목</TableCell>
                                            <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', padding: '8px' }}>세부항목</TableCell>
                                            <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', padding: '8px' }}>점검상태</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {record.checkType.map((type, typeIndex) => (
                                            type.checkRsltDtlList.map((detail, detailIndex) => (
                                                <TableRow key={`${detail.checkItemId}-${typeIndex}-${detailIndex}`}>
                                                    {detailIndex === 0 && (
                                                        <TableCell rowSpan={type.checkRsltDtlList.length} sx={{ padding: '8px', verticalAlign: 'middle' }}>
                                                            {type.checkTypeNm}
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ padding: '8px' }}>{detail.checkItemNm}</TableCell>
                                                    <TableCell sx={{ padding: '8px' }}>{detail.checkRsltValNm}</TableCell>
                                                </TableRow>
                                            ))
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', height: '300px', alignItems: 'center' }}>
                        <Typography sx={{ color: '#999' }}>조회된 점검 기록이 없습니다.</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
